<template>
    <div id="content-wrap" style="margin-left:90px;" :style="{ opacity: !$root.isLoadingData ? 1 : 0 }">
        <MissingDataText v-if="!players" message="Selected squad is empty" />
        <div v-else>
            <Header :updateSelection="refreshData" />
            <ActiveFilter />
            <div class="row mx-auto" style="width: 90vw;">
                <div class="col-12 col-lg-6 w-100 p-lg-0 pr-lg-2" v-if="playerData && activeComparisonData">
                    <LongitudinalKpiAnalysis :sessions="sessions" :data="playerData.kpiDataBySessionId" :comparisonKpiData="activeComparisonData.kpis"/>
                </div>
                <div class="col-12 col-lg-6 p-lg-0 pl-lg-2" v-if="playerData && activeComparisonData">
                    <LongitudinalSprintCount :sessions="sessions" :sprintData="playerData.sprintCounts.SPRINT" :hsrData="playerData.sprintCounts.HSR" 
                    :comparisonCountsData="activeComparisonData.sprintCounts" :speedTypeThresholds="speedTypeThresholds" :distanceBands="distanceBands" />
                </div>
                <div class="col-12 p-lg-0 px-3" v-if="playerData && activeComparisonData">
                    <LongitudinalPeakAnalysis :sessions="sessions" 
                        :rainbowData="playerData.peakPhysicalCapacityBySessionId"
                        :rainbowComparisonData="activeComparisonData.peakPhysicalCapacityRainbowData"
                        :peakEpochCountData="playerData.peakThresholdCountBySessionId"
                        :peakEpochCountComparisonData="activeComparisonData.peakThresholdCounts" />
                </div>
                <div class="col-12 p-lg-0 px-3">
                    <LongitudinalForceVelocity :sessions="sessions" :data="fvData" />
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Header from './Header';
import store from './store';
import filterStore from './filterStore';
import LongitudinalSprintCount from './LongitudinalSprintCount';
import LongitudinalPeakAnalysis from './LongitudinalPeakAnalysis';
import LongitudinalKpiAnalysis from './LongitudinalKpiAnalysis';
import LongitudinalForceVelocity from './LongitudinalForceVelocity';
import MissingDataText from "@/components/MissingDataText.vue";
import { errorHandler } from "@/components/ErrorHandler";
import { UserData } from "@/components/UserData";
import UpdateSetting from "@/utils/UpdateSetting";
import KpiOption from "@/utils/KpiOption";
import ActiveFilter from "./ActiveFilter.vue";
import { mapKpiCategory } from "@/utils/LongitudinalChartHelper";

export default {
    components: {
        MissingDataText,
        Header,
        LongitudinalKpiAnalysis,
        LongitudinalPeakAnalysis,
        LongitudinalSprintCount,
        LongitudinalForceVelocity,
        ActiveFilter
    },
    data() {
        return {
            customerId: UserData.customerId(),
            kpiSelected: KpiOption.defaultSelection,
            kpiOptions: KpiOption.options,
            sessions: null,
            sprintCounts: null,
            peakThresholdCounts: null,
            rainbowData: null,
            kpiData: null,
            earliestSession: null,
            fvData: null,
            playerData: null,
            leagueComparisonData: null,
            positionComparisonData: null,
            activeComparisonData: null,
            speedTypeThresholds: null,
            distanceBands: null
        };
    },
    methods: {
        async refreshData() {
            this.$root.executeTaskWithProgressBar(async () => {
                await filterStore.dispatch('initialiseFilters', { teamType: UserData.teamType(), position: this.selectedPlayer.position });
                if (!filterStore.state.periods) {
                    await filterStore.dispatch('updatePeriods', this);
                }
                await this.getData();
                this.activeComparisonData = this.leagueComparisonData;
                this.getFVData();
            });
        },
        async getData() {
            const startDate = this.selectedPeriod.startDate.toISOString().substring(0, 10);
            const endDate = this.selectedPeriod.endDate.toISOString().substring(0, 10);
            const response = await this.$root.webApiGet(
                `/longitudinalplayerdevelopment?customerId=${this.customerId}` +
                `&playerId=${this.playerId}` +
                `&matchCount=${this.selectedMatchCount}` +
                `&referenceCriteriaId=${this.selectedReferenceGroup.value}` +
                `&playerCriteriaId=${this.selectedPlayerCriteria.value}` +
                `&startDate=${startDate}` +
                `&endDate=${endDate}`);
            if (response.status === 200) {
                const data = response.data;
                this.sessions = data.sessions;
                this.playerData = data.playerData;
                this.leagueComparisonData = data.leagueComparisonData;
                this.positionComparisonData = data.positionComparisonData;
                this.speedTypeThresholds = data.speedTypeThresholds;
                this.distanceBands = data.distanceBands;
            } else {
                errorHandler.error(response, this);
            }
        },
        getFVData() {
            this.fvData = {
                force: mapKpiCategory(this.sessions, this.playerData.forcesBySessionId, this.activeComparisonData.force),
                velocity: mapKpiCategory(this.sessions, this.playerData.velocitiesBySessionId, this.activeComparisonData.velocity)
            };
        },
    },
    async mounted() {
        UpdateSetting();
        this.$root.executeTaskWithProgressBar(async () => {
            await store.dispatch('getPlayers', this);
            await this.refreshData();
            this.$root.newPageView("Longitudinal Player Development Page", UserData.userName());
        });
        console.log("LongitudinalAnalysis.vue mounted");
    },
    computed: {
        UserData() {
            return UserData;
        },
        players() {
            return store.state.players;
        },
        selectedPlayerIdx() {
            return store.state.selectedPlayerIdx;
        },
        selectedPlayer() {
            return store.state.selectedPlayer;
        },
        playerId() {
            return store.state.selectedPlayer.playerId;
        },
        selectedKpiTitle() {
            return this.kpiOptions.find(t => t.value === this.kpiSelected).text;
        },
        selectedPlayerCriteria() {
            return filterStore.state.selectedPlayerCriteria;
        },
        selectedReferenceGroup() {
            return filterStore.state.selectedReferenceGroup;
        },
        selectedPositionAverage() {
            return filterStore.state.selectedPositionAverage;
        },
        selectedPeriod() {
            return filterStore.state.selectedPeriod;
        },
        selectedMatchCount() {
            return filterStore.state.selectedMatchCount;
        },
        combinedSelection() {
            //ensure refreshData gets called once when there's a change to the filter selection
            return `${this.selectedPlayerCriteria.value}-${this.selectedReferenceGroup.value}-${this.selectedPeriod?.value}-${this.selectedMatchCount}`;
        },
    },
    watch: {
        combinedSelection() {
            this.refreshData();
        },
        selectedPositionAverage() {
            this.activeComparisonData = this.selectedPositionAverage === "avg" ? this.leagueComparisonData : this.positionComparisonData;
            this.getFVData();
        }
    },
};
</script>
<style>
.longitudinal-analysis-list li {
    margin-bottom: 10px;
}
</style>